import React, { useMemo } from "react";
import { TeamMatchupProps } from "./types";
import { darken } from "color2k";
import { topFourLeagues } from "components-sbk/src/components/Competitor/js/helpers";
import { TeamLogo } from "components-sbk/src/components/TeamLogo/TeamLogo";
import { Composer } from "components/src/components/Composer/Composer";
import type { ComposerElementType } from "components/src/components/Composer/types";
import {
  getTeamInfo,
  isValidTeamInfoObject,
  type TeamInfoReturnType,
} from "shared/src/hooks/getTeamInfo";
import cx from "components/src/utils/cx";
import { cssColor } from "shared/src/utils/cssUtils";
import { sizeArray, sizeMap } from "shared/src/styles/sizeStyles";
import clamp from "lodash-es/clamp";

function makeElement(team, jus = "topLeft", size) {
  const isTopFour = topFourLeagues.includes(team?.league);
  const color = team?.metadata?.colors?.primary?.value ?? "#666666";
  const primaryColor = jus !== "topLeft" ? darken(color, 0.2) : color;
  const secondaryColor = jus !== "topLeft" ? color : darken(color, 0.2);
  const _size = Number(size.replace("px", "")) - 8;
  const weight = 0.58;
  const computedScale = clamp((_size / 100) * weight * Math.log(_size), 0.75, 10);

  return {
    fg: {  
      element: isTopFour ? (
        <TeamLogo name={team?.name} size={_size} mode="dark" />
      ) : (
        <div className="display-xs cui-text-fg-global-white" style={{
            transform: `scale(${computedScale})`
          }}>
          {team?.abbreviation}
        </div>
      ),
      positioning: { justify: "center" },
    },
    bg: {
      primaryColor,
      secondaryColor,
      colorMode: "both",
      // colorType: "dark",
      gradient: "vertical",
      gradientSharpness: "blurry",
      className: "cui-overflow-hidden"
    },
    positioning: {
      position: "absolute",
      height: "50%",
      justify: jus,
    }
  } as ComposerElementType;
}

const vs = (size: string | number = "1px", useAt: boolean = false ) => {
  const _size = typeof size === "number" ? size : Number(size.replace("px", ""));

  const weight = 0.58;
  const computedScale = (_size / 100) * weight * Math.log(_size);

  return {
    fg: {
      element: (
        <div
          className="cui-w-2 cui-h-2 cui-p-2px cui-w-2 cui-h-2 cui-rounded-[9999px] cui-grid cui-place-content-center cui-bg-bg-layer-one body-xs cui-text-fg-default"
          style={{
            transform: `scale(${computedScale})`
          }}
        >
          {useAt ? "at" : "vs"}
        </div>
      ),
      positioning: { justify: "center" },
    },
  } as ComposerElementType;
}

const lineDivider = {
  fg: {
    element: <div className="cui-bg-fg-minimal cui-w-full cui-h-1px" />,
    positioning: { justify: "center", width: "100%", height: "1px" },
  },
} as ComposerElementType;

export const TeamMatchup = ({
  firstTeam,
  secondTeam,
  isLive,
  style,
  width = "46px",
  height = "46px",
  size,
  center,
  borderColor,
  useAt = false,
  className,
  ...rest
}: TeamMatchupProps) => {

  const { _firstTeam, _secondTeam } = useMemo(() => {
    if (isValidTeamInfoObject(firstTeam) && isValidTeamInfoObject(secondTeam))
      return { _firstTeam: firstTeam, _secondTeam: secondTeam };
    const teams: Record<string, TeamInfoReturnType | {}> = {
      firstTeam: {},
      secondTeam: {},
    };
    if (typeof firstTeam === "string") teams.firstTeam = getTeamInfo(firstTeam);
    if (typeof secondTeam === "string")
      teams.secondTeam = getTeamInfo(secondTeam);
    return {
      _firstTeam: teams.firstTeam as TeamInfoReturnType,
      _secondTeam: teams.secondTeam as TeamInfoReturnType,
    };
  }, [firstTeam, secondTeam]);

  const _style = useMemo(() => {
    if (borderColor) return { borderColor: cssColor(borderColor) };
    return {
      borderColor: isLive
        ? "var(--highlight-accent)"
        : "var(--fg-minimal)"
    };
  }, [isLive, _firstTeam]);

  const composerClasses = useMemo(()=>{
    return cx(
        {
          "cui-mx-auto": !!center,
        },
        `cui-border cui-border-solid cui-border-1 ${className}`
      );
  },[center, className]);

  const { _width, _height } = useMemo(()=>{
    const [isStr, isNum] = [typeof size === "string", typeof size === "number"];
    if (isStr || isNum) {
      const isSemanticSize = isStr && size && size in sizeArray;
      if (isSemanticSize) {
        const mappedSize = sizeMap.get(size);
        return { _width: mappedSize.width, _height: mappedSize.height }
      }
      if (isStr && (size as string).includes("px")) {
        return { _width: size, _height: size }
      }
      if (isNum) {
        return { _width: `${size}px`, _height: `${size}px`}
      }
    }
    return { _width: width, _height: height }
  },[size, width, height]);

  const _elements = useMemo(()=>{
      return [
        makeElement(_firstTeam, undefined, _width),
        makeElement(_secondTeam, "bottomLeft", _width),
        lineDivider,
        vs(_width, useAt),
      ]
  },[_width, _firstTeam, _secondTeam])

  if (!_firstTeam && !_secondTeam) return null;

  return (
    <Composer
      elements={_elements}
      positioning={{ position: "relative", width: _width, height: _height }}
      shape="circle"
      className={composerClasses}
      style={_style}
      {...rest}
    />
  );
};
